import React, { useState } from "react";
import { Landmark, Undo2, Loader2, ZoomIn } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { formatDate as fmtDate } from "@/lib/formatDate";
import { DocCompareDialog } from "@/components/DocCompareDialog";

const chf = (v) => (v == null ? "–" : `${Number(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`);

/** Blättern-Ansicht: Gutschrift/Rückgabe erkannt → Bank-Gutschrift, ursprünglicher Kauf, Umschalten. */
export function CreditNotePanel({ companyId, item, onUpdated, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const ex = item.extracted || {};
  const isCredit = !!ex.is_credit_note;

  const call = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = (await apiClient.post(`/companies/${companyId}/inbox/${item.id}/credit-note`, body)).data;
      toast.success(okMsg);
      if (r.warning) toast.warning(r.warning);
      onUpdated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  if (!isCredit) {
    if (!ex.credit_note_rejected) return null;
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-2.5 text-xs text-amber-900 space-y-1" data-testid="review-credit-rejected">
        <div className="inline-flex items-center gap-1.5 font-semibold"><Undo2 className="h-3.5 w-3.5" /> Gutschrift-Wortlaut erkannt, aber kein passender Kaufbeleg – als normaler Beleg behandelt</div>
        <button type="button" disabled={busy} onClick={() => call({ is_credit_note: true }, "Als Gutschrift markiert")} className="underline underline-offset-2" data-testid="review-credit-mark">Doch als Gutschrift behandeln</button>
      </div>
    );
  }
  const bank = item.bank_refund_match;
  const cands = item.credit_origin_candidates || [];
  const origId = ex.credit_of_receipt_id || cands[0]?.id || null;
  const origCand = cands.find((c) => c.id === origId) || null;
  const confirmFromCompare = async (withOrigin) => {
    setConfirming(true);
    try {
      const want = withOrigin ? origId : "";
      if ((ex.credit_of_receipt_id || "") !== (want || "")) {
        await apiClient.post(`/companies/${companyId}/inbox/${item.id}/credit-note`, { is_credit_note: true, original_receipt_id: want || "" });
      }
      setCompare(false);
      await onConfirm?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Bestätigen fehlgeschlagen"); }
    finally { setConfirming(false); }
  };
  return (
    <div className="rounded-lg border border-teal-300 bg-teal-50/70 p-3 text-xs text-teal-950 space-y-2" data-testid="review-credit-panel">
      <div className="inline-flex items-center gap-1.5 font-semibold text-sm">
        <Undo2 className="h-4 w-4" /> Gutschrift / Rückgabe – Geld kommt zurück (+{chf(ex.total_amount)})
      </div>
      <p className="opacity-90">Kein Aufwand: Beim Bestätigen wird die Rückerstattung als Aufwands-Korrektur auf {String(ex.suggested_account || "dem Aufwandskonto").slice(0, 4)} erfasst.</p>
      {bank ? (
        <div className="flex items-start gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-emerald-900" data-testid="review-bank-refund">
          <Landmark className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span><strong>Bank-Gutschrift gefunden</strong> · +{chf(bank.amount)} am {fmtDate(bank.date)} · «{(bank.description || "").slice(0, 80)}» – wird beim Bestätigen automatisch verknüpft.</span>
        </div>
      ) : (
        <div className="flex items-start gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-slate-600" data-testid="review-bank-refund-none">
          <Landmark className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>Noch keine passende Bank-Gutschrift (+{chf(ex.total_amount)}, ab {fmtDate(ex.invoice_date)}) im Bankabgleich – sie wird zugeordnet, sobald der Kontoauszug importiert ist.</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2" data-testid="review-credit-origin">
        <span className="text-slate-600">Ursprünglicher Kauf:</span>
        {cands.length > 0 || ex.credit_of_receipt_id ? (
          <select className="h-7 rounded-md border border-slate-300 bg-white px-1.5 text-xs max-w-md" value={ex.credit_of_receipt_id || ""} disabled={busy}
            onChange={(e) => call({ is_credit_note: true, original_receipt_id: e.target.value || null }, e.target.value ? "Mit Kaufbeleg verknüpft – gleiches Konto wie der Kauf" : "Verknüpfung entfernt")}
            data-testid="review-credit-origin-select">
            <option value="">– nicht verknüpfen –</option>
            {ex.credit_of_receipt_id && !cands.some((c) => c.id === ex.credit_of_receipt_id) && <option value={ex.credit_of_receipt_id}>{ex.credit_of_receipt_label || ex.credit_of_receipt_id}</option>}
            {cands.map((c) => (
              <option key={c.id} value={c.id}>{c.items_matched?.length ? `✓ Artikel ${c.items_matched.join(", ")} · ` : ""}{c.description} · {fmtDate(c.date)} · {chf(c.amount)}{c.exact_amount ? " · gleicher Betrag" : c.remaining < c.amount ? ` · offen ${chf(c.remaining)}` : ""}</option>
            ))}
          </select>
        ) : <span className="italic text-slate-500">kein Kaufbeleg dieses Händlers in den letzten 180 Tagen gefunden</span>}
        {(ex.credit_of_receipt_id || cands.length > 0) && (
          <button type="button" onClick={() => setCompare(true)} className="inline-flex items-center gap-1 rounded border border-teal-400 bg-white px-1.5 py-0.5 font-medium text-teal-800 hover:bg-teal-100" data-testid="review-credit-compare" title="Gutschrift und Kaufbeleg gross nebeneinander mit Lupe">
            <ZoomIn className="h-3 w-3" /> Lupe: beide vergleichen
          </button>
        )}
        {item.credit_item_terms?.length > 0 && !ex.credit_of_receipt_id && <span className="text-[11px] text-slate-500" data-testid="review-credit-items">Rückgabe: {item.credit_item_terms.join(", ")}</span>}
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      </div>
      <button type="button" disabled={busy} onClick={() => call({ is_credit_note: false }, "Wieder als normaler Kaufbeleg behandelt")} className="text-slate-600 underline underline-offset-2 hover:text-slate-900" data-testid="review-credit-unmark">
        Keine Gutschrift – als normalen Beleg behandeln
      </button>
      {compare && (
        <DocCompareDialog
          open
          onClose={() => setCompare(false)}
          title="Gutschrift und Kaufbeleg im Vergleich – gehört die Rückgabe zu diesem Kauf?"
          footer={(
            <div className="flex flex-wrap items-center justify-end gap-2 w-full" data-testid="review-credit-compare-footer">
              <button type="button" onClick={() => setCompare(false)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="review-credit-compare-close">Schliessen</button>
              {origId && (
                <button type="button" disabled={confirming} onClick={() => confirmFromCompare(false)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60" data-testid="review-credit-compare-confirm-noorigin" title="Gutschrift buchen, aber nicht mit diesem Kaufbeleg verknüpfen">
                  Nicht dieser Kauf – Gutschrift ohne Verknüpfung bestätigen
                </button>
              )}
              {onConfirm && (
                <button type="button" disabled={confirming} onClick={() => confirmFromCompare(!!origId)} className="inline-flex items-center gap-1.5 rounded-md bg-teal-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-60" data-testid="review-credit-compare-confirm">
                  {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
                  {origId ? "Ja, gehört zu diesem Kauf – Gutschrift bestätigen" : "Gutschrift bestätigen"}
                </button>
              )}
            </div>
          )}
          facts={[
            { key: "supplier", label: "Händler", values: [ex.supplier_name || "–", origCand?.description || "–"], diff: false },
            { key: "date", label: "Datum", values: [fmtDate(ex.invoice_date), fmtDate(origCand?.date)], diff: false },
            { key: "amount", label: "Betrag", values: [`+${chf(ex.total_amount)} (Rückerstattung)`, origCand?.amount != null ? `${chf(origCand.amount)}${Math.abs(Number(origCand.amount) - Number(ex.total_amount)) > 0.005 ? ` · Teilrückgabe, Differenz ${chf(Number(origCand.amount) - Number(ex.total_amount))}` : " · gleicher Betrag"}` : "–"], diff: origCand?.amount != null && Number(origCand.amount) + 0.005 < Number(ex.total_amount) },
            ...(item.credit_item_terms?.length ? [{ key: "items", label: "Zurückgegeben", values: [item.credit_item_terms.join(", "), origCand?.items_matched?.length ? `✓ ${origCand.items_matched.join(", ")} auf dem Kauf` : "nicht im Kauftext gefunden"], diff: !origCand?.items_matched?.length }] : []),
          ]}
          docs={[
            { id: item.id, title: item.file_name, fetchUrl: `/companies/${companyId}/inbox/${item.id}/file`, fileName: item.file_name, mime: item.content_type, tag: "Gutschrift / Rückgabe (dieser Beleg)", fitMode: "receipt", subtitle: `${ex.supplier_name || ""} · ${fmtDate(ex.invoice_date)} · +${chf(ex.total_amount)}` },
            ...(origId ? [{ id: origId, title: origCand?.description || ex.credit_of_receipt_label || "Kaufbeleg", fetchUrl: `/receipts/${origId}/file`, fileName: origCand?.file_name, tag: "Ursprünglicher Kauf", fitMode: "receipt", subtitle: `${fmtDate(origCand?.date)} · ${chf(origCand?.amount)}`, accent: true }] : []),
          ]}
        />
      )}
    </div>
  );
}
